import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Shuffle } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const SIZE = 8;
const INGREDIENTS = [
  { key: "prawn", ch: "🦐", g: "from-orange-400 to-red-500" },
  { key: "squid", ch: "🦑", g: "from-fuchsia-400 to-purple-600" },
  { key: "fish", ch: "🐟", g: "from-sky-400 to-blue-600" },
  { key: "broccoli", ch: "🥦", g: "from-green-400 to-emerald-600" },
  { key: "carrot", ch: "🥕", g: "from-amber-400 to-orange-500" },
  { key: "corn", ch: "🌽", g: "from-yellow-300 to-amber-500" },
];

let _id = 1;
const nid = () => _id++;
const randType = () => Math.floor(Math.random() * INGREDIENTS.length);
const makeTile = (type = randType(), special = null) => ({ id: nid(), type, special });

// ---- Web Audio sound effects ----
let _actx = null;
function tone(freq, dur = 0.12, type = "sine", vol = 0.22, when = 0) {
  try {
    _actx = _actx || new (window.AudioContext || window.webkitAudioContext)();
    const t = _actx.currentTime + when;
    const o = _actx.createOscillator(), g = _actx.createGain();
    o.connect(g); g.connect(_actx.destination); o.type = type; o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur); o.start(t); o.stop(t + dur + 0.03);
  } catch (e) { /* audio unsupported */ }
}
const sfx = {
  select: () => tone(520, 0.07, "triangle", 0.18),
  swap: () => tone(360, 0.08, "sine", 0.18),
  match: (chain = 1) => tone(440 + chain * 60, 0.12, "sine", 0.22),
  create: () => { tone(720, 0.09, "square", 0.2); tone(980, 0.11, "square", 0.2, 0.06); },
  bomb: () => { tone(180, 0.24, "sawtooth", 0.3); tone(90, 0.34, "sawtooth", 0.24, 0.08); },
};

function collectRuns(b) {
  const runs = [];
  for (let r = 0; r < SIZE; r++) {
    let c = 0;
    while (c < SIZE) {
      if (!b[r][c]) { c++; continue; }
      const t = b[r][c].type; let k = c;
      while (k < SIZE && b[r][k] && b[r][k].type === t) k++;
      if (k - c >= 3) runs.push({ dir: "h", r, c0: c, len: k - c, type: t });
      c = k;
    }
  }
  for (let c = 0; c < SIZE; c++) {
    let r = 0;
    while (r < SIZE) {
      if (!b[r][c]) { r++; continue; }
      const t = b[r][c].type; let k = r;
      while (k < SIZE && b[k][c] && b[k][c].type === t) k++;
      if (k - r >= 3) runs.push({ dir: "v", c, r0: r, len: k - r, type: t });
      r = k;
    }
  }
  return runs;
}
const runCells = (run) => run.dir === "h"
  ? Array.from({ length: run.len }, (_, i) => [run.r, run.c0 + i])
  : Array.from({ length: run.len }, (_, i) => [run.r0 + i, run.c]);

function initBoard() {
  const b = Array.from({ length: SIZE }, () => Array.from({ length: SIZE }, () => makeTile()));
  let guard = 0;
  while (collectRuns(b).length > 0 && guard++ < 200) {
    for (const run of collectRuns(b)) for (const [r, c] of runCells(run)) b[r][c] = makeTile();
  }
  return b;
}

// Compute one clear step. seed = extra cells to force-clear (bomb swap). swapCell = where specials are created.
function stepClear(b, swapCell, seed) {
  const runs = collectRuns(b);
  if (runs.length === 0 && (!seed || seed.size === 0)) return null;
  const cleared = new Set(seed || []);
  runs.forEach((run) => runCells(run).forEach(([r, c]) => cleared.add(`${r},${c}`)));

  const creates = new Map();
  let madeStriped = false, madeBomb = false;
  runs.forEach((run) => {
    if (run.len < 4) return;
    const cl = runCells(run);
    let target = cl[Math.floor(cl.length / 2)];
    if (swapCell && cl.some(([r, c]) => r === swapCell.r && c === swapCell.c)) target = [swapCell.r, swapCell.c];
    const key = `${target[0]},${target[1]}`;
    if (run.len >= 5) { creates.set(key, { type: run.type, special: "bomb" }); madeBomb = true; }
    else { creates.set(key, { type: run.type, special: run.dir === "h" ? "striped-v" : "striped-h" }); madeStriped = true; }
  });

  // Activate special candies that fall within the cleared set (cascade activation)
  let explosion = false, changed = true;
  while (changed) {
    changed = false;
    for (const key of Array.from(cleared)) {
      const [r, c] = key.split(",").map(Number);
      const tile = b[r][c];
      if (!tile || !tile.special) continue;
      if (tile.special === "striped-h") { for (let cc = 0; cc < SIZE; cc++) { const k = `${r},${cc}`; if (!cleared.has(k)) { cleared.add(k); changed = true; } } explosion = true; }
      else if (tile.special === "striped-v") { for (let rr = 0; rr < SIZE; rr++) { const k = `${rr},${c}`; if (!cleared.has(k)) { cleared.add(k); changed = true; } } explosion = true; }
      else if (tile.special === "bomb") { for (let rr = 0; rr < SIZE; rr++) for (let cc = 0; cc < SIZE; cc++) { if (b[rr][cc] && b[rr][cc].type === tile.type) { const k = `${rr},${cc}`; if (!cleared.has(k)) { cleared.add(k); changed = true; } } } explosion = true; }
    }
  }
  // Keep creation cells (they become special candies, not removed)
  creates.forEach((_v, key) => cleared.delete(key));
  const points = cleared.size * 10 + (madeStriped ? 60 : 0) + (madeBomb ? 120 : 0);
  return { cleared, creates, points, explosion };
}

const makeRandomFill = () => {
  const t = makeTile();
  const roll = Math.random();
  if (roll < 0.02) t.special = "bomb";
  else if (roll < 0.17) t.special = Math.random() < 0.5 ? "striped-h" : "striped-v";
  return t;
};

function applyGravity(b) {
  for (let c = 0; c < SIZE; c++) {
    const col = [];
    for (let r = SIZE - 1; r >= 0; r--) if (b[r][c]) col.push(b[r][c]);
    for (let r = SIZE - 1; r >= 0; r--) b[r][c] = col[SIZE - 1 - r] || makeRandomFill();
  }
}

const cloneBoard = (b) => b.map((row) => row.map((t) => (t ? { ...t } : null)));
const adjacent = (a, b) => Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;
const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export default function Match3({ onScore, onExplosion, initialScore = 0 }) {
  const [board, setBoard] = useState(initBoard);
  const [sel, setSel] = useState(null);
  const [score, setScore] = useState(initialScore);
  const [combo, setCombo] = useState(0);
  const [flash, setFlash] = useState(false);
  const [clearing, setClearing] = useState(new Set());
  const busy = useRef(false);
  const scoreRef = useRef(initialScore);
  const started = useRef(false);

  useEffect(() => {
    if (!started.current) { scoreRef.current = initialScore; setScore(initialScore); }
  }, [initialScore]);

  const bump = useCallback((pts) => {
    started.current = true;
    scoreRef.current += pts;
    setScore(scoreRef.current);
    onScore?.(scoreRef.current);
  }, [onScore]);

  const resolve = useCallback(async (startBoard, swapCell, seed) => {
    let b = cloneBoard(startBoard);
    let chain = 0;
    let sc = swapCell, sd = seed;
    while (true) {
      const res = stepClear(b, sc, sd);
      sc = null; sd = null;
      if (!res) break;
      chain++; setCombo(chain);
      if (res.explosion) { setFlash(true); onExplosion?.(); sfx.bomb(); setTimeout(() => setFlash(false), 450); }
      else sfx.match(chain);
      setClearing(res.cleared);
      await delay(res.explosion ? 380 : 220);
      res.cleared.forEach((key) => { const [r, c] = key.split(",").map(Number); b[r][c] = null; });
      res.creates.forEach((v, key) => { const [r, c] = key.split(",").map(Number); if (b[r][c]) b[r][c] = { ...b[r][c], type: v.type, special: v.special }; });
      if (res.creates.size) sfx.create();
      setBoard(cloneBoard(b));
      bump(res.points * chain);
      await delay(120);
      applyGravity(b);
      setClearing(new Set());
      setBoard(cloneBoard(b));
      await delay(180);
    }
    setCombo(0);
    return b;
  }, [bump, onExplosion]);

  const clickTile = async (r, c) => {
    if (busy.current) return;
    if (!sel) { setSel({ r, c }); sfx.select(); return; }
    if (sel.r === r && sel.c === c) { setSel(null); return; }
    if (!adjacent(sel, { r, c })) { setSel({ r, c }); sfx.select(); return; }
    busy.current = true;
    const b = cloneBoard(board);
    const tmp = b[sel.r][sel.c]; b[sel.r][sel.c] = b[r][c]; b[r][c] = tmp;
    setBoard(b); sfx.swap();
    const from = { r: sel.r, c: sel.c };
    setSel(null);
    await delay(160);

    const A = b[from.r][from.c], B = b[r][c];
    let seed = null;
    if ((A && A.special === "bomb") || (B && B.special === "bomb")) {
      seed = new Set();
      const other = A && A.special === "bomb" ? B : A;
      const bothBomb = A && A.special === "bomb" && B && B.special === "bomb";
      const color = bothBomb ? null : other?.type;
      for (let rr = 0; rr < SIZE; rr++) for (let cc = 0; cc < SIZE; cc++) {
        const tt = b[rr][cc];
        if (!tt) continue;
        if (color === null || tt.type === color || tt.special === "bomb") seed.add(`${rr},${cc}`);
      }
    }

    if (seed || collectRuns(b).length > 0) {
      await resolve(b, { r, c }, seed);
    } else {
      const bb = cloneBoard(b);
      const t2 = bb[from.r][from.c]; bb[from.r][from.c] = bb[r][c]; bb[r][c] = t2;
      setBoard(bb);
    }
    busy.current = false;
  };

  const shuffle = () => { if (busy.current) return; setSel(null); setBoard(initBoard()); sfx.swap(); };

  return (
    <div className="relative">
      <div className="mb-3 grid grid-cols-2 gap-2 sm:gap-3">
        <Stat label="Score" value={score.toLocaleString("id-ID")} testid="game-score" glow="gold" />
        <Stat label="Combo" value={`x${combo || 1}`} testid="game-combo" glow="red" />
      </div>
      <button data-testid="game-shuffle-btn" onClick={shuffle}
        className="mb-3 flex w-full touch-btn items-center justify-center gap-2 rounded-xl border border-gold/40 bg-gold/10 py-2.5 text-sm font-bold text-gold transition-all hover:bg-gold/20 active:scale-95">
        <Shuffle size={16} /> Acak Warna Permen
      </button>

      <div
        data-testid="match3-board"
        className={`relative grid rounded-2xl border border-white/10 bg-black/50 p-1.5 sm:p-2 transition-all ${flash ? "fire-pulse ring-4 ring-gold" : ""}`}
        style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0,1fr))`, gap: "4px", touchAction: "manipulation", perspective: "900px" }}
      >
        {board.map((row, r) =>
          row.map((tile, c) => {
            if (!tile) return <div key={`empty-${r}-${c}`} className="aspect-square rounded-lg bg-black/20" />;
            const isSel = sel && sel.r === r && sel.c === c;
            const isClearing = clearing.has(`${r},${c}`);
            const info = INGREDIENTS[tile.type] || {};
            const sp = tile.special;
            return (
              <motion.button
                key={tile.id}
                data-testid={`match3-board-tile-${r}-${c}`}
                onClick={() => clickTile(r, c)}
                layout
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className={`tile-pop relative aspect-square rounded-lg bg-gradient-to-br ${info.g} flex items-center justify-center
                  text-xl sm:text-2xl md:text-3xl shadow-md ${isClearing ? "tile-explode" : ""} ${sp ? "ring-2 ring-white/70" : ""}`}
                style={{
                  lineHeight: 1,
                  transformStyle: "preserve-3d",
                  transform: isSel ? "translateZ(24px) rotateX(14deg) rotateY(-14deg) scale(1.1)" : "none",
                  boxShadow: isSel ? "0 14px 26px rgba(0,0,0,0.55), 0 0 0 3px rgba(255,255,255,0.9)" : undefined,
                  zIndex: isSel ? 20 : 1,
                }}
              >
                {/* Striped candy overlay (distinct from Candy Crush: diagonal double-stripe) */}
                {(sp === "striped-h" || sp === "striped-v") && (
                  <span className="pointer-events-none absolute inset-[3px] rounded-md opacity-80"
                    style={{ background: sp === "striped-h"
                      ? "repeating-linear-gradient(0deg, rgba(255,255,255,0.95) 0 3px, transparent 3px 7px)"
                      : "repeating-linear-gradient(90deg, rgba(255,255,255,0.95) 0 3px, transparent 3px 7px)" }} />
                )}
                {/* Color bomb: dark orb with sparkle ring (distinct hex look) */}
                {sp === "bomb" ? (
                  <span className="relative flex h-full w-full items-center justify-center">
                    <span className="absolute inset-[2px] rounded-md bg-black/85 ring-2 ring-white/80" />
                    <span className="relative text-base sm:text-lg" style={{ filter: "drop-shadow(0 0 4px #fff)" }}>✨</span>
                  </span>
                ) : (
                  <span className="relative drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">{info.ch}</span>
                )}
              </motion.button>
            );
          })
        )}
      </div>

      {flash && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.3, 1], opacity: [0, 1, 0] }}
            transition={{ duration: 0.7 }} className="flex flex-col items-center">
            <img src={LOGO_URL} alt="" className="h-24 w-24 rounded-full bg-white/90 p-1" />
            <div className="mt-2 font-display text-lg font-extrabold uppercase tracking-tight text-gold drop-shadow-lg">
              Restaurant Explosion!
            </div>
          </motion.div>
        </div>
      )}

      <p className="mt-3 text-center text-xs text-muted-foreground">
        Ketuk 2 ikon bersebelahan untuk menukar · <b className="text-gold">4 sejajar</b> = Permen Bergaris · <b className="text-gold">5 sejajar</b> = Bom Warna 🔥
      </p>
    </div>
  );
}

function Stat({ label, value, testid, glow }) {
  return (
    <div className={`rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-center ${glow === "gold" ? "gold-glow" : "red-glow"}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div data-testid={testid} className="font-mono-num text-lg font-bold text-white">{value}</div>
    </div>
  );
}
